import { CellModel, CellSource, ColumnSource, ConditionalFormat, RequestFilters, RowSource, RowModel } from "@omegagrid/grid";
import { dom, ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { Style as DefStyle } from "@reporting/rp-types/definition";
import { RuleList } from "../../components";

const PAGE_SIZE = 500;

export class StylesGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _styles: DefStyle[];
	private editedRow: RowModel;

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 3,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}, {
				action: 'duplicate',
				title: msg('Duplicate'),
				icon: 'copy',
				color: ColorTypes.Yellow
			}, {
				action: 'edit',
				title: msg('Edit'),
				icon: 'pen',
				color: ColorTypes.Blue
			}],
			filter: false,
			sorting: false,
		}, {
			data: 'id_style',
			header: '#',
			t: 'n',
			e: false,
			w: 60
		}, {
			data: 'description',
			header: msg('Description'),
			t: 't',
			e: true,
			w: 200
		}, {
			data: 'style',
			header: msg('Style'),
			t: 't',
			e: true,
			w: 300
		}, {
			data: 'who_created',
			header: msg('Created by'),
			t: 't',
			e: false,
			w: 180
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			e: false,
			w: 145
		}, {
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		this._styles = await rp.ms('definition').get<DefStyle[]>(`styles/R`, this.createRequestParams(filters, {
			fields: ['idz', 'id_style', 'description', 'style', 'who_created', 'date_created'],
			order: [['id_style', 'asc']],
			count: PAGE_SIZE
		}));
		return this._styles.map(data => ({ data }));
	}

	getCells(): CellSource[] {
		const cells: CellSource[] = [];
		let parsingError = false;
		this._styles.forEach((style, index) => {
			if (!style.style) return;
			let cfs: ConditionalFormat[] = [];
			try {
				cfs = JSON.parse(style.style);
			} catch (e) {
				parsingError = true;
				console.error('Failed to parse style:', e);
			}

			let s = null;
			let dv = style.style;
			if (cfs.length === 1 && !cfs[0].condition && (!cfs[0].type || cfs[0].type === 'formula')) {
				s = cfs[0]?.style;
				dv = msg('static style');
			} else if (!parsingError) {
				dv = `${cfs.length} ${msg('rule/s')}`;
			}

			if (parsingError) cells.push({ r: index, c: 2, s: { color: 'ff0000' } });
			cells.push({ r: index, c: 3, v: style.style, s: s, dv: dv });
		});
		if (parsingError) rp.host.bucket.showAlert({ content: msg('Failed to parse some styles.'), type: 'danger' });
		return cells;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			pageSize: PAGE_SIZE,
			refreshAfterSave: true,
		});
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign({_state: row.state, _index: row.index}, row.data));
		await rp.ms('definition').post('styles/R', data);
	}

	onAction(action: string, cell: CellModel): void {
		switch (action) {
			case 'edit':
				if (!cell.row.data) {
					rp.host.bucket.showAlert({content: msg('Save the row before editing.'), type: 'warning'});
					return;
				}
				this.editedRow = cell.row;
				rp.host.openModalSidebar({
					component: this.getStyleEditorComponent(cell.row.data.style),
					caption: `${cell.row.data.description ? cell.row.data.description : `${msg('Style')} #${cell.row.data.id_style}`}`
				});
				break;
		}
	}

	getStyleEditorComponent(origStyle: string): HTMLElement {
		const component = dom.createElement<RuleList>('rp-definition-rules');
		const conditionalFormats: ConditionalFormat[] = origStyle ? JSON.parse(origStyle) : [];
		conditionalFormats.forEach((format: ConditionalFormat) => { format.type = format.type || 'formula' });
		component.conditionalFormats = conditionalFormats;
		component.onSave = this.setStyle.bind(this);
		return component;
	}

	setStyle(newStyle: ConditionalFormat[]) {
		const r = this.editedRow.index;
		const c = this.editedRow.cells.findIndex(cell => cell.column.data == 'style');
		if (c < 0) return;
		this.editor.setCellValue(r, c, newStyle.length ? JSON.stringify(newStyle) : null);

	}
}
